import { useState, useEffect, useCallback } from "react";
import type { TaktBoardResponse } from "../../../models";
import type { V2Schedule } from "../../../models";
import { fetchV2Schedules, fetchTaktBoard } from "../../../api/clientV2";

/**
 * Fetches schedules list and loads Takt board data for the first schedule.
 * Manages loading/error state for the async lifecycle.
 */
export function useScheduleData() {
  const [schedules, setSchedules] = useState<V2Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TaktBoardResponse | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Load schedules
  useEffect(() => {
    fetchV2Schedules()
      .then((s) => {
        setSchedules(s);
        if (s.length > 0) setSelectedScheduleId(s[0].id);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not connect to API v2 (port 5051). Is it running?");
        setLoading(false);
      });
  }, []);

  // Load takt board data
  useEffect(() => {
    if (!selectedScheduleId) return;
    // Only show loading spinner on initial load, not on reloads,
    // so the board stays mounted and scroll position is preserved.
    if (!data) setLoading(true);
    fetchTaktBoard(selectedScheduleId)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load Takt board data.");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScheduleId, reloadTrigger]);

  const reload = () => setReloadTrigger((n) => n + 1);

  const updateBulkEntryDates = useCallback(
    (updates: Array<{ activityIds: number[]; newStart: string; newFinish: string }>) => {
      setData((prev) => {
        if (!prev) return prev;

        const updateMap = new Map<number, { newStart: string; newFinish: string }>();
        for (const u of updates) {
          for (const aid of u.activityIds) {
            updateMap.set(aid, { newStart: u.newStart, newFinish: u.newFinish });
          }
        }

        let changed = false;
        const newSections = prev.sections.map((section) => {
          let sectionChanged = false;
          const newGroups = section.groups.map((group) => {
            let groupChanged = false;
            const newEntries = group.entries.map((entry) => {
              const update = updateMap.get(entry.activity_ids[0]);
              if (update) {
                groupChanged = true;
                return { ...entry, start: update.newStart, finish: update.newFinish };
              }
              return entry;
            });
            if (groupChanged) {
              sectionChanged = true;
              return { ...group, entries: newEntries };
            }
            return group;
          });
          if (sectionChanged) {
            changed = true;
            return { ...section, groups: newGroups };
          }
          return section;
        });

        return changed ? { ...prev, sections: newSections } : prev;
      });
    },
    []
  );

  const updateEntryDates = useCallback(
    (activityIds: number[], newStart: string, newFinish: string) => {
      updateBulkEntryDates([{ activityIds, newStart, newFinish }]);
    },
    [updateBulkEntryDates]
  );

  return {
    schedules,
    selectedScheduleId,
    loading,
    error,
    data,
    reload,
    updateEntryDates,
    updateBulkEntryDates,
  };
}
